import { useEffect, useRef, useState, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';

interface GameState {
  gameId?: string;
  userHand: any[];
  aiHand: any[];
  communityCards: any[];
  userChips: number;
  aiChips: number;
  pot: number;
  currentRound: string;
  currentActor: string;
  currentBet: number;
  userBet: number;
  aiBet: number;
  status: string;
  winner?: string;
  winningHand?: any;
}

export function useGameSocket(authToken: string | null) {
  const socketRef = useRef<Socket | null>(null);
  const [connected, setConnected] = useState(false);
  const [gameState, setGameState] = useState<GameState | null>(null);
  const [error, setError] = useState<string | null>(null);

  const resetState = useCallback(() => {
    setGameState(null);
    setError(null);
  }, []);

  useEffect(() => {
    if (!authToken) return;

    const socket = io('/game', {
      auth: { token: authToken },
      transports: ['websocket'],
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      setConnected(true);
      setError(null);
    });

    socket.on('disconnect', () => {
      setConnected(false);
    });

    socket.on('game_state', (state: GameState) => {
      setGameState(state);
    });

    socket.on('game_ended', (state: GameState) => {
      setGameState(state);
    });

    socket.on('error', (err: { code: string; message: string }) => {
      setError(err.message);
    });

    return () => {
      socket.disconnect();
    };
  }, [authToken]);

  const joinGame = useCallback((gameId: string) => {
    socketRef.current?.emit('join_game', { gameId });
  }, []);

  const sendAction = useCallback((gameId: string, action: string, amount?: number) => {
    socketRef.current?.emit('player_action', { gameId, action, amount });
  }, []);

  return {
    socket: socketRef.current,
    connected,
    gameState,
    error,
    joinGame,
    sendAction,
    resetState,
  };
}
